import {put, call, all} from 'redux-saga/effects';
import Role from "../../entities/role";
import * as Actions from "../actions/roleActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type RoleAction = {
    roleId: string;
    role: Role;
    roles: Role[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneRole(action: RoleAction) {
	const session = tabSession.captureGeneration();
	try {
		const role: Role = yield call(api.findOne, 'role', action.roleId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ROLE_FETCH_ONE_SUCCESS, roleId: action.roleId, role, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ROLE_FETCH_ONE_FAILURE, roleId: action.roleId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {RoleAction} action
 */
export function* saveRole(action: RoleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ROLE_SAVE saga', action.role?.id);

		if (action.role === undefined || action.role === null) {
			console.error('saveRole received a null');
			throw new Error('saveRole received a null');
		}

		if (!(action.role instanceof Role)) {
			action.role = new Role(action.role);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.role.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.role);
			const addResult = yield call(idb.add, 'role', action.role);
			if (addResult.isErr()) {
				console.error(`⮂ saveRole: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertRoleSave(action.role, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.ROLE_SAVE_FAILURE, role: action.role, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.ROLE_SAVE, role: action.role, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertRoleSave(action.role, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.ROLE_SAVE_FAILURE, role: action.role, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {RoleAction} action
 * @param {Role[]} action.roles
 */
export function* saveManyRoles(action: RoleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ROLE_SAVE_MANY saga', action.roles.map(role => role.id).join(','));

        if (!Array.isArray(action.roles)) {
            console.error('saveManyRoles expected an array, received', action.roles);
            throw new Error('saveManyRoles expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveRole), under these queue keys
		const dispatchedAddedOns: number[] = action.roles.map(role => role?.addedOn);
		const revertAll = () => action.roles.map((role, i) =>
			put(Actions.revertRoleSave(role, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Role[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.roles.length; i++) {
				const role = action.roles[i];
				role.persisted = false;
				ensureOperationId(role);
				toSave.push(role);
			}

			const addResult = yield call(idb.add, 'role', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyRoles: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.ROLE_SAVE_MANY_FAILURE, roles: action.roles, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.ROLE_SAVE_MANY, roles: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.ROLE_SAVE_MANY_FAILURE, roles: action.roles, status: 500, err});

			console.error(`Error saving ${action.roles.length} roles to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {RoleAction} action
 */
export function* removeRole(action: RoleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.role === undefined || action.role === null || !(action.role instanceof Role)) {
			console.error('removeRole expected type Role, received', action.role);
			reportQueued(false);
			throw new Error('removeRole expected type Role');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.role.status = 'D';
		action.role.addedOn = newHRTimestamp();
		action.role.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.role);
		const addResult = yield call(idb.add, 'role', action.role);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeRole: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.ROLE_REMOVE_FAILURE, role: action.role, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ROLE_REMOVE, role: action.role});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ROLE_REMOVE_FAILURE, role: action.role, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Role.
	*
	* @param {RoleAction} action
	*/
export function* restoreRole(action: RoleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreRole expected getCurrentUserId() to return a value');
        return;
    }

	if (action.role === undefined || action.role === null) {
        console.error('restoreRole expected a Role, received null');
        throw new Error('restoreRole expected a Role, received null');
    }
    else if (!(action.role instanceof Role)) action.role = new Role(action.role); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.role.status = 'U';
		action.role.addedOn = newHRTimestamp();
		action.role.addedBy = userId;
		action.role.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.role);
		const addResult = yield call(idb.add, 'role', action.role);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreRole: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ROLE_RESTORE_FAILURE, role: action.role, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ROLE_RESTORE, role: action.role});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ROLE_RESTORE_FAILURE, role: action.role, status: 500, err});

		console.error('Error restoring from database', err);
	}
}